import React, { useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Container, Form, Row, Col } from "react-bootstrap";
import { SvgIcon, useTheme } from '@mui/material';
import ChartMetrics from '../icons/chart-metrics.svg?react';
import { StyledRadioInput, TextInput, SelectInput } from "../../../Helpers/Inputs/index";
import { IDAHO, KENTUCKY, FLORIDA, COLORADO, SOUTH_CAROLINA, IOWA } from '../../../Helpers/Constants/States';
import { object, string, array as yuparray, mixed, date } from '../../../Helpers/Validators/customValidators';
import { emptyStringToUndefined } from '../../../Helpers/Formatters/EmptyStringToUndefined.jsx';
import InsurancePolicyForm from './InsurancePolicyForm';
import BackNext from '../../Common/SubComponents/BackNext';

const selectedStateList = [KENTUCKY, SOUTH_CAROLINA];

export default function ReplacingInfo(props) {

    const theme = useTheme()
    const getreasonOfReplacementOptions = () => {
        return (
            [
                { value: "Additional Benefits", label: "Additional Benefits" },
                { value: "Fewer Benefits and Lower Premiums", label: "Fewer Benefits and Lower Premiums" },
                { value: "No Change in Benefits, But Lower Premiums", label: "No Change in Benefits, But Lower Premiums" },
                { value: "Other (Specify)", label: "Other (Specify)" },
            ]
        )
    }
    const state = props.basicInfo.state;
    const getNumberOfPoliciesOptions = () => {
        const policyOptions = [
            { value: 1, label: "1" },
            { value: 2, label: "2" },
        ]
        if (selectedStateList.includes(state)) {
            policyOptions.unshift({ value: 0, label: "0" });
        }
        return policyOptions;
    }
    const formMethods = useForm({
        resolver: yupResolver(ReplacingInfo.ValidationSchema),
        mode: 'all',
        reValidateMode: 'all',
        defaultValues: props.formState
    });

    const { handleSubmit, trigger, register, setValue } = formMethods;

    const handleValueChange = (field, value) => {
        props.onFieldChange(field, value);
        trigger(field);
    };

    useEffect(() => {
        if (props.formState.numberOfPolicies === '0' && (state !== KENTUCKY && state !== SOUTH_CAROLINA)) {
            handleValueChange("numberOfPolicies", '');
        }
    }, [])

    useEffect(() => {
        register('state');
        setValue('state', state);
    }, [register])

    const handleNumberOfPolicyChange = (policyNumber) => {
        const numberOfExistingPolicies = Number(policyNumber);
        const insurancePolicies = props.formState.insurancePolicies.length;
        const totalPolicyToUpdate = props.formState.insurancePolicies;
        if (numberOfExistingPolicies >= 0) {
            if (insurancePolicies > numberOfExistingPolicies) {
                for (let i = 0; i < insurancePolicies - numberOfExistingPolicies; i++) {
                    totalPolicyToUpdate.pop();
                }
                props.setMultiField(totalPolicyToUpdate, policyNumber);
                setValue('insurancePolicies', totalPolicyToUpdate);
                setValue('numberOfPolicies', policyNumber);
            }
            else if (insurancePolicies < numberOfExistingPolicies) {
                addNewInsurancePolicy(policyNumber, numberOfExistingPolicies - insurancePolicies);
            }
            else {
                props.onFieldChange('numberOfPolicies', policyNumber);
            }
        }
    }

    const onSubmit = () => {
        if (props.formState.existingCoverageWithAflac === "Yes") {
            props.goToStep();
        }
        else {
            props.onNext();
        }
    };

    const addNewInsurancePolicy = (value, numberOfNewForm) => {
        const newForm = props.formState.insurancePolicies;
        for (let i = 0; i < numberOfNewForm; i++) {
            const newData = ReplacingInfo.DefaultFormState().insurancePolicies[0];
            newForm.push(newData);
        }

        props.setMultiField(newForm, value);
    }

    const forms = props.formState.insurancePolicies.map((insurancePoliciesFormState, index) =>
        <InsurancePolicyForm
            formState={insurancePoliciesFormState}
            onChange={(newForm) => {
                props.onFieldChange(
                    'insurancePolicies',
                    props.formState.insurancePolicies.map((form, formIndex) => {
                        return formIndex === index ? newForm : form;
                    })
                );
            }}
            trigger={trigger}
            key={index}
            index={index}
            state={state}
            existingHealthInsurance={props.formState.existingHealthInsurance}
            replaceExistingCoverage={props.formState.replaceExistingCoverage}
            existingSimilarHealthInsurance={props.formState.existingSimilarHealthInsurance}
        />
    )

    let replaceExistingCoverageLabel;
    if (state !== IDAHO && state !== IOWA) {
        replaceExistingCoverageLabel = 'Is this insurance intended to replace any other health insurance now in force?'
    }
    if (state === IDAHO || state === IOWA) {
        replaceExistingCoverageLabel = 'Is this insurance intended to replace any other accident and sickness insurance presently in force?'
    }

    return (
        <Container fluid className='border-class'>
            <FormProvider {...formMethods}>
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <Row className="justify-content-center pt-3">
                        <Col lg={5} className='w-100'>
                            <div className="text-center mb-2 pt-4">
                                <SvgIcon
                                    sx={{
                                        '& path': {
                                            stroke: theme.palette.primary.main, // override stroke directly
                                        },
                                    }}
                                    component={ChartMetrics}
                                    className="small-icons"
                                    fontSize="large"
                                />
                            </div>
                            <div className="text-center mb-4 form-title">Replacement Information</div>

                            {state === COLORADO &&
                                <Fragment>
                                    <div className='mb-3'>
                                        <p className="section-title">Do you have another accident and sickness insurance policy in force?</p>
                                        <StyledRadioInput
                                            name="existingHealthInsurance"
                                            id="existingHealthInsurance"
                                            value={props.formState.existingHealthInsurance}
                                            options={[{ value: "Yes", label: "Yes" }, { value: "No", label: "No" }]}
                                            onChange={e => handleValueChange('existingHealthInsurance', e)}
                                            isWhiteBackground={true}
                                            isBlankIcon={true}
                                        />
                                    </div>

                                    {props.formState.existingHealthInsurance === "Yes" &&
                                        <div className='mb-3'>
                                            <TextInput
                                                name="company"
                                                id="company"
                                                label="Company Name"
                                                value={props.formState.company}
                                                onChange={(e) => handleValueChange('company', e.target.value)}
                                            />
                                        </div>
                                    }

                                    {props.formState.existingHealthInsurance === "Yes" && state === COLORADO &&
                                        <div className='mb-3'>
                                            <p className="section-title">Do you intend to replace your current accident and sickness insurance policy with this policy?	</p>
                                            <StyledRadioInput
                                                name="replaceExistingAccidentCoverage"
                                                id="replaceExistingAccidentCoverage"
                                                value={props.formState.replaceExistingAccidentCoverage}
                                                options={[{ value: "Yes", label: "Yes" }, { value: "No", label: "No" }]}
                                                onChange={e => handleValueChange('replaceExistingAccidentCoverage', e)}
                                                isWhiteBackground={true}
                                                isBlankIcon={true}
                                            />
                                        </div>}

                                    <div className='mb-3'>
                                        <p className="section-title">Do you have any other accident and sickness insurance that provides benefits similar to this accident and sickness policy?</p>
                                        <StyledRadioInput
                                            name="existingSimilarHealthInsurance"
                                            id="existingSimilarHealthInsurance"
                                            value={props.formState.existingSimilarHealthInsurance}
                                            options={[{ value: "Yes", label: "Yes" }, { value: "No", label: "No" }]}
                                            onChange={e => handleValueChange('existingSimilarHealthInsurance', e)}
                                            isWhiteBackground={true}
                                            isBlankIcon={true}
                                        />
                                    </div>

                                    {props.formState.existingSimilarHealthInsurance === "Yes" &&
                                        <React.Fragment>
                                            <div className='mb-3'>
                                                <TextInput
                                                    name="similarCompany"
                                                    id="similarCompany"
                                                    label="Company Name"
                                                    value={props.formState.similarCompany}
                                                    onChange={(e) => handleValueChange('similarCompany', e.target.value)}
                                                />
                                            </div>
                                            <div className='mb-3'>
                                                <TextInput
                                                    name="replaceExistingCoverageType"
                                                    id="replaceExistingCoverageType"
                                                    label="Policy Type"
                                                    value={props.formState.replaceExistingCoverageType}
                                                    onChange={(e) => handleValueChange('replaceExistingCoverageType', e.target.value)}
                                                />
                                            </div>
                                        </React.Fragment>
                                    }
                                </Fragment>
                            }
                            <div className='mb-3'>
                                <p className="section-title">{replaceExistingCoverageLabel}</p>
                                <StyledRadioInput
                                    name="replaceExistingCoverage"
                                    id="replaceExistingCoverage"
                                    value={props.formState.replaceExistingCoverage}
                                    options={[{ value: "Yes", label: "Yes" }, { value: "No", label: "No" }]}
                                    onChange={e => handleValueChange('replaceExistingCoverage', e)}
                                    isWhiteBackground={true}
                                    isBlankIcon={true}
                                />
                            </div>

                            {(state === COLORADO && props.formState.replaceExistingCoverage === "Yes") &&
                                <div className='mb-3'>
                                    <SelectInput
                                        name="reasonOfReplacement"
                                        id="reasonOfReplacement"
                                        value={props.formState.reasonOfReplacement}
                                        onChange={e => handleValueChange('reasonOfReplacement', e.target.value)}
                                        label="Reason For Replacement"
                                        selectOptions={getreasonOfReplacementOptions()}
                                    />
                                </div>
                            }

                            {(state === COLORADO && props.formState.replaceExistingCoverage === "Yes" && props.formState.reasonOfReplacement === "Other (Specify)") &&
                                <div className='mb-3'>
                                    <TextInput
                                        name="reasonOfReplacementSpecify"
                                        id="reasonOfReplacementSpecify"
                                        label="Other (Specify)"
                                        value={props.formState.reasonOfReplacementSpecify}
                                        onChange={(e) => handleValueChange('reasonOfReplacementSpecify', e.target.value)}
                                    />
                                </div>
                            }

                            {state === SOUTH_CAROLINA && <p className="section-title">Please list all accident and health insurance you have in force:</p>}
                            {state === KENTUCKY && <p className="section-title">List all health insurance now in force:</p>}
                            {(state === FLORIDA && props.formState.replaceExistingCoverage === "Yes") && <p className="section-title">Please provide the company name, policy number and issue date of the policies being replaced:</p>}

                            {((selectedStateList.includes(state)) || (state === FLORIDA && props.formState.replaceExistingCoverage === "Yes")) &&
                                <div className='mb-3'>
                                    <SelectInput
                                        name="numberOfPolicies"
                                        id="numberOfPolicies"
                                        value={props.formState.numberOfPolicies}
                                        onChange={e => {
                                            handleNumberOfPolicyChange(e.target.value)
                                        }}
                                        label="Number of Policies"
                                        selectOptions={getNumberOfPoliciesOptions()}
                                    />
                                </div>
                            }

                            {props.formState.numberOfPolicies >= 1 ? forms : null}
                        </Col>
                    </Row>

                    <Row className="justify-content-center second-section replacemnt-hospital-info">
                        <Col lg={5}>
                            <div className='mb-3'>
                                <p className="section-title">Do you have any other hospital confinement indemnity or hospital confinement sickness indemnity coverage with Aflac?</p>
                                <StyledRadioInput
                                    name="existingCoverageWithAflac"
                                    id="existingCoverageWithAflac"
                                    value={props.formState.existingCoverageWithAflac}
                                    options={[{ value: "Yes", label: "Yes" }, { value: "No", label: "No" }]}
                                    onChange={e => handleValueChange('existingCoverageWithAflac', e)}
                                    isWhiteBackground={true}
                                    isBlankIcon={true}
                                />
                            </div>
                            {/* As per new requrement commented the code */}
                            {/* <div className='mb-3'>
                            <p className="section-title">Do you have both hospital confinement indemnity and hospital confinement sickness indemnity coverage in force now?</p>
                            <StyledRadioInput
                                name="existingCoverageBoth"
                                id="existingCoverageBoth"
                                value={props.formState.existingCoverageBoth}
                                options={[{ value: "Yes", label: "Yes" }, { value: "No", label: "No" }]}
                                onChange={e => handleValueChange('existingCoverageBoth', e)}
                                errors={errors && errors.existingCoverageBoth}
                                isWhiteBackground={true}
                                isBlankIcon={true}
                            />
                        </div>

                        {props.formState.existingCoverageBoth === "Yes" &&
                            <Fragment>
                                <div className='mb-3'>
                                    <TextInput
                                        name="policyNumberHCI"
                                        id="policyNumberHCI"
                                        label="Hospital Confinement Indemnity Insurance Policy Number"
                                        value={props.formState.policyNumberHCI}
                                        errors={errors && errors.policyNumberHCI}
                                        onChange={(e) => handleValueChange('policyNumberHCI', e.target.value)}
                                        inputRef={register}
                                    />
                                </div>
                                <div className='mb-3'>
                                    <TextInput
                                        name="policyNumberHCSI"
                                        id="policyNumberHCSI"
                                        label="Hospital Confinement Sickness Indemnity Insurance Policy Number"
                                        value={props.formState.policyNumberHCSI}
                                        errors={errors && errors.policyNumberHCSI}
                                        onChange={(e) => handleValueChange('policyNumberHCSI', e.target.value)}
                                        inputRef={register}
                                    />
                                </div>
                            </Fragment>
                        } */}

                            {(state === COLORADO) &&
                                <Fragment>
                                    <p className="section-title">Are you covered for medical assistance through the state Medicaid program, Health First Colorado:</p>
                                    <div className='mb-3'>
                                        <p className="section-title">As a Specified Low-Income Medicare Beneficiary (SLMB)?</p>
                                        <StyledRadioInput
                                            name="existingMedicaidSLMB"
                                            id="existingMedicaidSLMB"
                                            value={props.formState.existingMedicaidSLMB}
                                            options={[{ value: "Yes", label: "Yes" }, { value: "No", label: "No" }]}
                                            onChange={e => handleValueChange('existingMedicaidSLMB', e)}
                                            isWhiteBackground={true}
                                            isBlankIcon={true}
                                        />
                                    </div>

                                    <div className='mb-3'>
                                        <p className="section-title">As a Qualified Medicare Beneficiary (QMB)?</p>
                                        <StyledRadioInput
                                            name="existingMedicaidQMB"
                                            id="existingMedicaidQMB"
                                            value={props.formState.existingMedicaidQMB}
                                            options={[{ value: "Yes", label: "Yes" }, { value: "No", label: "No" }]}
                                            onChange={e => handleValueChange('existingMedicaidQMB', e)}
                                            isWhiteBackground={true}
                                            isBlankIcon={true}
                                        />
                                    </div>

                                    <div className='mb-3'>
                                        <p className="section-title">For other Medicaid medical benefits?</p>
                                        <StyledRadioInput
                                            name="existingMedicaidOther"
                                            id="existingMedicaidOther"
                                            value={props.formState.existingMedicaidOther}
                                            options={[{ value: "Yes", label: "Yes" }, { value: "No", label: "No" }]}
                                            onChange={e => handleValueChange('existingMedicaidOther', e)}
                                            isWhiteBackground={true}
                                            isBlankIcon={true}
                                        />
                                    </div>
                                </Fragment>
                            }
                        </Col>
                    </Row>
                    <div className='backnext-wrapper'>
                        <BackNext onBack={props.onBack} isAflac={true} continueLabel={{ text: "Next" }} />
                    </div>
                </Form>
            </FormProvider>
        </Container>
    );
}

ReplacingInfo.propTypes = {
    formState: PropTypes.object.isRequired,
    onNext: PropTypes.func,
    onFieldChange: PropTypes.func.isRequired,
    onBack: PropTypes.func,
};

ReplacingInfo.ValidationSchema = object().shape({
    replaceExistingCoverage: string().required("This field is required."),
    replaceExistingAccidentCoverage: string().when(
        ['state', 'existingHealthInsurance'],
        ([state, existingHealthInsurance], schema) => {
            return state === COLORADO && existingHealthInsurance === "Yes"
                ? schema.required('This field is required.')
                : string().notRequired();
        }
    ),
    existingHealthInsurance: string().when('state', {
        is: COLORADO,
        then: (schema) => schema.required("This field is required."),
        otherwise: (schema) => schema.notRequired()
    }),
    existingSimilarHealthInsurance: string().when('state', {
        is: COLORADO,
        then: (schema) => schema.required("This field is required."),
        otherwise: (schema) => schema.notRequired()
    }),
    company: string().when(
        ['state', 'existingHealthInsurance'],
        ([state, existingHealthInsurance], schema) => {
            return state === COLORADO && existingHealthInsurance === "Yes"
                ? schema.required('Company name is required.')
                : string().notRequired();
        }
    ),
    replaceExistingCoverageType: string().when(
        ['state', 'existingSimilarHealthInsurance'],
        ([state, existingSimilarHealthInsurance], schema) => {
            return state === COLORADO && existingSimilarHealthInsurance === "Yes"
                ? schema.required('Type of policy is required.')
                : string().notRequired();
        }
    ),
    similarCompany: string().when(
        ['state', 'existingSimilarHealthInsurance'],
        ([state, existingSimilarHealthInsurance], schema) => {
            return state === COLORADO && existingSimilarHealthInsurance === "Yes"
                ? schema.required('Company name is required.')
                : string().notRequired();
        }
    ),
    existingCoverageWithAflac: string().required("This field is required."),
    // existingCoverageBoth: string().required("This field is required."),
    // policyNumberHCI: yupnumber().when('existingCoverageBoth', {
    //     is: "Yes",
    //     then: yupnumber()
    //         .transform(emptyStringToUndefined)
    //         .required("Hospital Confinement Indemnity Number is required.")
    //         .typeError("Must be a number."),
    //     otherwise: yupnumber()
    //         .transform(emptyStringToUndefined)
    //         .notRequired()
    // }),
    // policyNumberHCSI: yupnumber().when('existingCoverageBoth', {
    //     is: "Yes",
    //     then: yupnumber()
    //         .transform(emptyStringToUndefined)
    //         .required("Hospital Confinement Sickness Indemnity Number is required.")
    //         .typeError("Must be a number."),
    //     otherwise: yupnumber()
    //         .transform(emptyStringToUndefined)
    //         .notRequired()
    // }),
    existingMedicaidSLMB: string().when('state', {
        is: COLORADO,
        then: (schema) => schema.required("This field is required."),
        otherwise: (schema) => schema.notRequired()
    }),
    existingMedicaidQMB: string().when('state', {
        is: COLORADO,
        then: (schema) => schema.required("This field is required."),
        otherwise: (schema) => schema.notRequired()
    }),
    existingMedicaidOther: string().when('state', {
        is: COLORADO,
        then: (schema) => schema.required("This field is required."),
        otherwise: (schema) => schema.notRequired()
    }),
    reasonOfReplacement: string().when(
        ['state', 'replaceExistingCoverage'],
        ([state, replaceExistingCoverage], schema) => {
            return state === COLORADO && replaceExistingCoverage === "Yes"
                ? schema.required('Reason of replacement is required.')
                : string().notRequired();
        }
    ),
    reasonOfReplacementSpecify: string().when(
        ['reasonOfReplacement', 'replaceExistingCoverage'],
        ([reasonOfReplacement, replaceExistingCoverage], schema) => {
            return reasonOfReplacement === "Other (Specify)" && replaceExistingCoverage === "Yes"
                ? schema.required('Reason of replacement is required.')
                : string().notRequired();
        }
    ),
    numberOfPolicies: string().when(
        ['state', 'replaceExistingCoverage'],
        ([state, replaceExistingCoverage], schema) => {
            return (selectedStateList.includes(state)) || (state[0] === FLORIDA && replaceExistingCoverage === "Yes")
                ? schema.required('This field is required.')
                : string().notRequired();
        }
    ),
    insurancePolicies: mixed().when(
        ['state', 'replaceExistingCoverage'],
        ([state, replaceExistingCoverage], schema) => {
            return state === FLORIDA && replaceExistingCoverage === "Yes"
                ? yuparray(
                    object().shape({
                        company: string().required("Company Name is required."),
                        policyNumber: string().required("Policy Number is required."),
                        insuranceDate: date().transform(emptyStringToUndefined)
                            .typeError('Invalid date.')
                            .required('Insurance date is required.')
                    })
                )
                : mixed().notRequired();
        }
    ),
});

ReplacingInfo.DefaultFormState = (prefill = false) => {
    return {
        replaceExistingCoverage: prefill ? "No" : '',
        existingHealthInsurance: '',
        existingSimilarHealthInsurance: '',
        company: '',
        replaceExistingCoverageType: '',
        similarCompany: '',
        numberOfPolicies: '',
        insurancePolicies: [
            {
                company: '',
                policyNumber: '',
                faceAmount: '',
                insuranceDate: '',
                replaceExistingCoverageType: '',
            }
        ],
        existingCoverageWithAflac: prefill ? "No" : '',
        // existingCoverageBoth: prefill ? "No" : '',
        //policyNumberHCI: '',
        //policyNumberHCSI: '',
        existingMedicaidSLMB: '',
        existingMedicaidQMB: '',
        existingMedicaidOther: '',
        reasonOfReplacement: '',
        reasonOfReplacementSpecify: '',
        replaceExistingAccidentCoverage: ""
    };
};
